import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SiteSettingsContext } from '@rsc/contexts/SiteSettingsContext';
import { styles } from '@utils';

import breakpoints from '@rsc/ui-components/breakpoints';
import PageWrapper from '@rsc/ui-components/page-wrapper/PageWrapper';

import { LocalizedLink } from '@rsc/gatsby-localization-helpers';
import { useSportdogInternationalContext } from '@util/static-translations';
import { showDownloadsList } from '@util/page-helpers/showDownloadsList';
import { showWhereToBuyLink } from '@util/page-helpers/showWhereToBuyLink';
import { getLocaleFlag } from '@util/helpers/localeHelpers';
import FacebookIcon from '../svgs/fb.svg';
import InstagramIcon from '../svgs/instagram.svg';
import YouTubeIcon from '../svgs/yt.svg';

import NewsletterSignup from './NewsletterSignupFooter';
import SocialIcons from './SocialIcons';
import FooterColumn from './FooterColumn';
import LegalFooter from './LegalFooter';
import DownloadsList from './DownloadsList';

const Container = styled.footer`
  background-color: ${styles.colors.gray[900]};
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  @media (min-width: ${breakpoints.md}) {
    flex-direction: row;
    flex-wrap: wrap;
  }
`;

const StyledLink = styled(LocalizedLink)`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${styles.colors.gray[400]};
  text-transform: uppercase;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
  > svg {
    margin-right: 0.5rem;
  }
`;

const FooterLink = styled.a`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${styles.colors.gray[400]};
  text-transform: uppercase;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

function Footer({ useQueryStringForEmailSignup }) {
  const { tt, locale, currency } = useSportdogInternationalContext({});

  const { siteSettings } = useContext(SiteSettingsContext);

  const getSocialLinks = useMemo(() => {
    const socialLinks = [];

    if (!siteSettings?.socialMedia) return socialLinks;

    if (siteSettings?.socialMedia.facebookBool) {
      socialLinks.push({
        title: 'Facebook',
        link: siteSettings?.socialMedia.facebookUrl,
        icon: FacebookIcon,
        iconAltText: 'Facebook',
      });
    }

    if (siteSettings?.socialMedia.instagramBool) {
      socialLinks.push({
        title: 'Instagram',
        link: siteSettings?.socialMedia.instagramUrl,
        icon: InstagramIcon,
        iconAltText: 'Instagram',
      });
    }

    if (siteSettings?.socialMedia.youtubeBool) {
      socialLinks.push({
        title: 'YouTube',
        link: siteSettings?.socialMedia.youtubeUrl,
        icon: YouTubeIcon,
        iconAltText: 'YouTube',
      });
    }

    return socialLinks;
  }, [siteSettings]);

  const removeSupportSite = ['en-gb', 'fr-fr', 'en-au'];
  return (
    <Container>
      <PageWrapper background="inherit">
        <Inner>
          <FooterColumn
            heading={tt(s => s.layoutCommon.footerCustomerCareHeader)}
          >
            {showWhereToBuyLink(locale) && (
              <StyledLink to="/where-to-buy">
                {tt(s => s.layoutCommon.findRetailerLink)}
              </StyledLink>
            )}
            {!removeSupportSite.includes(locale) && (
              <FooterLink
                href="https://support.sportdog.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {tt(s => s.layoutCommon.supportSiteLink)}
              </FooterLink>
            )}
            <StyledLink to="/international">
              <img src={getLocaleFlag(locale)} style={{ width: 31 }} alt="" />
              &nbsp;
              {tt(s => s.layoutCommon.changeCountryLink, {
                currencyCode: currency,
              })}
            </StyledLink>
          </FooterColumn>

          {showDownloadsList(locale) && (
            <FooterColumn
              heading={tt(s => s.layoutCommon.footerDownloadsHeader)}
            >
              <DownloadsList />
            </FooterColumn>
          )}

          {getSocialLinks.length > 0 && (
            <FooterColumn
              heading={tt(s => s.layoutCommon.footerFindUsOnHeader)}
            >
              <SocialIcons socialLinks={getSocialLinks} />
            </FooterColumn>
          )}

          <FooterColumn
            heading={tt(s => s.layoutCommon.footerEmailUpdatesHeader)}
          >
            <NewsletterSignup useQueryString={useQueryStringForEmailSignup} />
          </FooterColumn>
        </Inner>

        <LegalFooter />
      </PageWrapper>
    </Container>
  );
}

Footer.propTypes = {
  useQueryStringForEmailSignup: PropTypes.bool,
};

Footer.defaultProps = {
  useQueryStringForEmailSignup: false,
};

export default Footer;
